/**
 * AdminAuthContext (dual-mode: password OR Keycloak, chosen by accountsEnabled)
 *
 * Admin state is read from the backend `GET /api/admin-auth.php`, which is the
 * single authority in BOTH modes — it dispatches on the same `accountsEnabled`
 * flag (password/TRUSSADMIN session when off; Keycloak realm roles, or a
 * break-glass session, when on). We re-probe when the SSO identity changes so a
 * native Keycloak login reflects here without a page reload.
 *
 * Login actions ARE mode-dependent:
 *   - password mode  → loginWithPassword() posts the admin password.
 *   - keycloak mode  → loginKeycloak() redirects to SSO; loginWithPassword() is
 *                      the break-glass recovery (SUPER_ADMIN_BREAKGLASS_PASSWORD).
 *
 * Public shape is stable for consumers (isAdminAuthenticated / role /
 * isSuperAdmin / isChecking / logout); only RequireAdmin uses the login methods.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useAppSettings } from './AppSettingsContext';

const API_URL = process.env.REACT_APP_API_URL || '';

export type AdminRole = 'super' | 'admin' | null;
export type AdminAuthMode = 'password' | 'keycloak';

interface AdminAuthContextValue {
  isAdminAuthenticated: boolean;
  /** Granted tier — null when unauthenticated / not an admin. */
  role: AdminRole;
  /** Convenience: role === 'super'. Settings + migrate.php are super-only. */
  isSuperAdmin: boolean;
  /** True while the initial state probe (and SSO hydration) is in flight. */
  isChecking: boolean;
  /** How admins sign in right now — drives the RequireAdmin UI. */
  mode: AdminAuthMode;
  /**
   * Password mode: the admin password. Keycloak mode: the break-glass recovery
   * password. Returns true on success; throws only on a network error.
   */
  loginWithPassword: (password: string) => Promise<boolean>;
  /** Keycloak mode normal path — redirect to SSO, returning to /admin. */
  loginKeycloak: () => void;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

const normalizeRole = (raw: unknown): AdminRole =>
  raw === 'super' || raw === 'admin' ? raw : null;

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isChecking: authChecking, login: kcLogin, logout: kcLogout } = useAuth();
  const { settings } = useAppSettings();
  const mode: AdminAuthMode = settings.accountsEnabled ? 'keycloak' : 'password';

  const [role, setRole] = useState<AdminRole>(null);
  const [probing, setProbing] = useState(true);

  const probe = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin-auth.php`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRole(data.authenticated ? normalizeRole(data.role) : null);
      } else {
        setRole(null);
      }
    } catch {
      setRole(null); // network error → treat as not-admin; the gate will prompt
    } finally {
      setProbing(false);
    }
  }, []);

  // Probe on mount, when the mode flips, and when the SSO identity changes.
  useEffect(() => {
    setProbing(true);
    probe();
  }, [probe, mode, user?.sub]);

  const loginWithPassword = useCallback(async (password: string): Promise<boolean> => {
    const res = await fetch(`${API_URL}/api/admin-auth.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (res.ok) {
      try {
        const data = await res.json();
        setRole(normalizeRole(data.role) ?? 'admin');
      } catch {
        setRole('admin'); // tolerate a non-JSON success
      }
      return true;
    }
    return false;
  }, []);

  const loginKeycloak = useCallback(() => {
    kcLogin('/admin');
  }, [kcLogin]);

  const logout = useCallback(async (): Promise<void> => {
    if (mode === 'keycloak') {
      await kcLogout(); // ends the unified SSO session (or opens the confirm modal)
      setRole(null);
      return;
    }
    try {
      await fetch(`${API_URL}/api/admin-auth.php`, { method: 'DELETE', credentials: 'include' });
    } catch {
      /* ignore — clear local state regardless */
    }
    setRole(null);
  }, [mode, kcLogout]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      isAdminAuthenticated: role !== null,
      role,
      isSuperAdmin: role === 'super',
      isChecking: probing || (mode === 'keycloak' && authChecking),
      mode,
      loginWithPassword,
      loginKeycloak,
      logout,
    }),
    [role, probing, mode, authChecking, loginWithPassword, loginKeycloak, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  return ctx;
}
